import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Bookmark, Sparkles, MessageSquare, Phone, Target,
  Briefcase, PartyPopper, Archive, ChevronDown, Search, IdCard,
  Send, Loader2, X, Filter, Users, Building2, Star, MapPin,
  LayoutGrid,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../contexts/UserProfileContext';
import { showToast, handleApiError, RateLimitInfo } from '../utils/errorHandler';
import RateLimitNotification from '../components/RateLimitNotification';
import {
  RecruiterLoadingScreen,
  RecruiterPageShell,
} from '../components/Recruiter/RecruiterStates';
import RecruiterLoggedOutGate from '../components/RecruiterLoggedOutGate';
import AtsPushButton from '../components/AtsPushButton';
import PassportViewer from '../components/Passport/PassportViewer';
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface PipelineCandidate {
  id: string;
  cv_id: string;
  candidate_id?: string;
  stage: string;
  job_title: string;
  source: 'interested' | 'saved' | 'inbox';
  current_role?: string;
  current_level?: string;
  base_locations?: string;
  preferred_locations?: string;
  role_preferences?: string;
  cv_language?: string;
  conversation_id?: string;
  reach_out_id?: string;
  is_interested?: boolean;
  saved_at?: number;
  created_at?: number;
  explorer_id?: string;
}

const STAGES = [
  { key: 'interested', icon: Heart, label: 'Interested', color: 'pink', gradient: 'from-pink-500 to-rose-500' },
  { key: 'saved', icon: Bookmark, label: 'Saved', color: 'slate', gradient: 'from-slate-600 to-slate-800' },
  { key: 'candidate_sparked', icon: Sparkles, label: 'Sparked', color: 'purple', gradient: 'from-purple-500 to-violet-500' },
  { key: 'in_talks', icon: MessageSquare, label: 'In Talks', color: 'blue', gradient: 'from-blue-500 to-indigo-500' },
  { key: 'initial_call_scheduled', icon: Phone, label: 'Call Scheduled', color: 'cyan', gradient: 'from-cyan-500 to-teal-500' },
  { key: 'interviewing', icon: Target, label: 'Interviewing', color: 'yellow', gradient: 'from-yellow-500 to-amber-500' },
  { key: 'offer_talks', icon: Briefcase, label: 'Offer', color: 'orange', gradient: 'from-orange-500 to-amber-600' },
  { key: 'hired', icon: PartyPopper, label: 'Hired', color: 'emerald', gradient: 'from-emerald-500 to-green-500' },
  { key: 'currently_not_best_fit', icon: Archive, label: 'Not Best Fit', color: 'slate', gradient: 'from-slate-400 to-slate-500' },
] as const;

const STAGE_BG: Record<string, string> = {
  interested: 'bg-white border-slate-200',
  saved: 'bg-white border-slate-200',
  candidate_sparked: 'bg-white border-slate-200',
  in_talks: 'bg-white border-slate-200',
  initial_call_scheduled: 'bg-white border-slate-200',
  interviewing: 'bg-white border-slate-200',
  offer_talks: 'bg-white border-slate-200',
  hired: 'bg-white border-slate-200',
  currently_not_best_fit: 'bg-white border-slate-200',
};

const Pipeline: React.FC = () => {
  const { t } = useTranslation('hiringBoard');
  const navigate = useNavigate();
  const { apiCall, user, authReady, isLoading: authLoading } = useAuth();
  const { userProfile } = useUserProfile();
  const isRecruiter = !!userProfile?.is_recruiter;

  const [candidates, setCandidates] = useState<PipelineCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [jobFilter, setJobFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  const [passportCandidate, setPassportCandidate] = useState<PipelineCandidate | null>(null);

  useEffect(() => {
    if (!user || !authReady || !isRecruiter) return;
    loadPipeline();
    (async () => {
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/companies/my-company`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.company?.id) setCompanyId(data.company.id);
        }
      } catch { /* ignore */ }
    })();
  }, [user?.userId, authReady, isRecruiter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPipeline = async () => {
    setLoading(true);
    const all: PipelineCandidate[] = [];

    const [interestedRes, savedRes, inboxRes] = await Promise.allSettled([
      apiCall(`${API_BASE}/api/v1/companies/interested-feed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: '', top_k: 50 }),
      }).then((r) => (r.ok ? r.json() : null)),

      apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`).then((r) =>
        r.ok ? r.json() : null
      ),

      apiCall(`${API_BASE}/api/v1/recruiter/inbox/${user!.userId}`).then((r) =>
        r.ok ? r.json() : null
      ),
    ]);

    // Interested
    if (interestedRes.status === 'fulfilled' && interestedRes.value?.success) {
      for (const c of interestedRes.value.candidates || []) {
        all.push({
          id: `int_${c.cv_id}`,
          cv_id: c.cv_id,
          candidate_id: c.explorer_id,
          stage: 'interested',
          job_title: c.role_preferences || 'General Interest',
          source: 'interested',
          current_role: c.current_role,
          current_level: c.current_level,
          base_locations: c.base_locations,
          preferred_locations: c.preferred_locations,
          role_preferences: c.role_preferences,
          cv_language: c.cv_language,
          is_interested: true,
          explorer_id: c.explorer_id,
        });
      }
    }

    // Saved CVs - enrich via saved-candidates to get candidate_id
    if (savedRes.status === 'fulfilled' && savedRes.value) {
      const rawSaved = savedRes.value.saved_cvs || [];
      const savedCvIds = rawSaved
        .filter((s: any) => !all.some((c) => c.cv_id === s.cv_id))
        .map((s: any) => s.cv_id);

      let savedEnrichMap: Record<string, any> = {};
      if (savedCvIds.length > 0) {
        try {
          const BATCH = 50;
          for (let i = 0; i < savedCvIds.length; i += BATCH) {
            const chunk = savedCvIds.slice(i, i + BATCH);
            const enrichResp = await apiCall(`${API_BASE}/api/v1/companies/saved-candidates`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ cv_ids: chunk }),
            });
            if (enrichResp.ok) {
              const enrichData = await enrichResp.json();
              for (const c of enrichData.candidates || []) {
                savedEnrichMap[c.cv_id] = c;
              }
            }
          }
        } catch { /* best-effort */ }
      }

      for (const s of rawSaved) {
        const alreadyInPipeline = all.some((c) => c.cv_id === s.cv_id);
        if (!alreadyInPipeline) {
          const enriched = savedEnrichMap[s.cv_id];
          all.push({
            id: `sav_${s.cv_id}`,
            cv_id: s.cv_id,
            candidate_id: enriched?.explorer_id,
            stage: 'saved',
            job_title: s.prompt || 'Saved',
            source: 'saved',
            saved_at: s.saved_at,
            current_role: enriched?.current_role,
            current_level: enriched?.current_level,
            base_locations: enriched?.base_locations,
            preferred_locations: enriched?.preferred_locations,
            role_preferences: enriched?.role_preferences,
            cv_language: enriched?.cv_language,
            explorer_id: enriched?.explorer_id,
          });
        }
      }
    }

    // Inbox (reach-outs with statuses)
    if (inboxRes.status === 'fulfilled' && inboxRes.value?.clusters) {
      const seenCvIds = new Set(all.map((c) => c.cv_id));
      for (const cluster of inboxRes.value.clusters) {
        for (const job of cluster.jobs || []) {
          const m = job.metadata;
          const cvId = m.cv_id || job.id;
          all.push({
            id: `inbox_${job.id}`,
            cv_id: cvId,
            candidate_id: m.candidate_id || m.user_id,
            stage: m.status || 'candidate_sparked',
            job_title: m.job_title || cluster.name,
            source: 'inbox',
            conversation_id: m.conversation_id,
            reach_out_id: job.id,
            created_at: m.timestamp,
            cv_language: m.cv_language,
            current_role: m.current_role,
            base_locations: m.base_locations,
            explorer_id: m.candidate_id || m.user_id,
          });
          seenCvIds.add(cvId);
        }
      }
    }

    setCandidates(all);
    setLoading(false);

    // Enrich candidates missing profile data (saved + inbox)
    const idsToEnrich = all
      .filter((c) => !c.current_role && c.candidate_id)
      .map((c) => c.candidate_id!)
      .filter((id, i, arr) => arr.indexOf(id) === i);

    if (idsToEnrich.length > 0) {
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/profiles/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_ids: idsToEnrich }),
        });
        if (resp.ok) {
          const data = await resp.json();
          const profileMap: Record<string, any> = {};
          for (const p of data.profiles || []) profileMap[p.id] = p;

          setCandidates((prev) =>
            prev.map((c) => {
              if (!c.candidate_id) return c;
              const p = profileMap[c.candidate_id];
              if (!p) return c;
              return {
                ...c,
                cv_id: p.cv_id || c.cv_id,
                current_role: p.current_role || c.current_role,
                current_level: p.current_level || c.current_level,
                base_locations: p.base_locations || c.base_locations,
                preferred_locations: p.preferred_locations || c.preferred_locations,
                role_preferences: p.role_preferences || c.role_preferences,
                cv_language: p.cv_language || c.cv_language,
              };
            })
          );
        }
      } catch { /* profiles enrichment is best-effort */ }
    }
  };

  const allJobs = useMemo(() => {
    const jobs = new Set<string>();
    candidates.forEach((c) => {
      if (c.job_title && c.job_title !== 'General Interest' && c.job_title !== 'Saved') {
        jobs.add(c.job_title);
      }
    });
    return Array.from(jobs).sort();
  }, [candidates]);

  const filtered = useMemo(() => {
    let result = candidates;
    if (jobFilter !== 'all') {
      result = result.filter((c) => c.job_title === jobFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.job_title?.toLowerCase().includes(q) ||
          c.current_role?.toLowerCase().includes(q) ||
          c.base_locations?.toLowerCase().includes(q) ||
          c.role_preferences?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [candidates, jobFilter, searchQuery]);

  const stageGroups = useMemo(() => {
    const groups: Record<string, PipelineCandidate[]> = {};
    for (const s of STAGES) groups[s.key] = [];
    for (const c of filtered) {
      if (groups[c.stage]) groups[c.stage].push(c);
      else groups['candidate_sparked']?.push(c);
    }
    return groups;
  }, [filtered]);

  const updateStatus = async (candidate: PipelineCandidate, newStatus: string) => {
    if (!candidate.reach_out_id || candidate.source !== 'inbox') return;
    setUpdatingStatus(candidate.id);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/recruiter/reach-out/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vector_id: candidate.reach_out_id, status: newStatus }),
      });
      if (resp.ok) {
        setCandidates((prev) =>
          prev.map((c) => (c.id === candidate.id ? { ...c, stage: newStatus } : c))
        );
      }
    } catch {
      showToast('Failed to update status', 'error');
    }
    setUpdatingStatus(null);
  };

  // Non-recruiter / not logged in
  if (authLoading) {
    return <RecruiterLoadingScreen icon={LayoutGrid} />;
  }

  if (!user) {
    return <RecruiterLoggedOutGate returnTo="/pipeline" />;
  }

  if (!isRecruiter) {
    return <RecruiterLoggedOutGate returnTo="/pipeline" showLogin={false} />;
  }

  if (loading) {
    return <RecruiterLoadingScreen icon={LayoutGrid} />;
  }

  return (
    <RecruiterPageShell wide>
        {rateLimitInfo && (
          <div className="mb-4">
            <RateLimitNotification rateLimitInfo={rateLimitInfo} feature="pipeline" onClose={() => setRateLimitInfo(null)} />
          </div>
        )}

        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
          {/* Job Filter */}
          <div className="relative">
            <select
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              className="appearance-none pl-8 pr-8 py-1.5 text-sm font-medium bg-white border border-slate-200 rounded-lg text-slate-700 hover:border-slate-300 focus:outline-none focus:border-slate-300 focus:ring-1 focus:ring-inset focus:ring-slate-900/10 cursor-pointer"
            >
              <option value="all">All Jobs ({allJobs.length})</option>
              {allJobs.map((j) => (
                <option key={j} value={j}>
                  {j} ({candidates.filter((c) => c.job_title === j).length})
                </option>
              ))}
            </select>
            <Filter className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search candidates..."
              className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 hover:border-slate-300 focus:outline-none focus:border-slate-300 focus:ring-1 focus:ring-inset focus:ring-slate-900/10"
            />
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Summary */}
          <div className="ml-auto text-sm text-slate-500">
            <span className="font-semibold text-slate-700">{filtered.length}</span> candidates
          </div>
        </div>

        {(
          /* Kanban Board */
          <div className="overflow-x-auto pb-4 -mx-4 px-4">
            <div className="flex gap-4 min-w-max">
              {STAGES.map((stage) => {
                const items = stageGroups[stage.key] || [];
                const Icon = stage.icon;
                const hasItems = items.length > 0;

                return (
                  <div
                    key={stage.key}
                    className={`w-[280px] shrink-0 rounded-xl border ${hasItems ? STAGE_BG[stage.key] : 'border-slate-200 bg-slate-50/80'} transition-all`}
                  >
                    {/* Stage Header */}
                    <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3">
                      <div className="flex size-7 items-center justify-center rounded-lg bg-slate-100">
                        <Icon className="size-3.5 text-slate-600" />
                      </div>
                      <span className="text-[13px] font-semibold tracking-tight text-slate-800">{stage.label}</span>
                      <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-bold ${
                        hasItems ? 'bg-slate-100 text-slate-700' : 'bg-slate-100 text-slate-400'
                      }`}>
                        {items.length}
                      </span>
                    </div>

                    {/* Cards */}
                    <div className="px-2 pb-2 space-y-2 max-h-[calc(100vh-240px)] overflow-y-auto">
                      {items.length === 0 ? (
                        <div className="py-8 text-center">
                          <p className="text-[11px] text-slate-400">No candidates</p>
                        </div>
                      ) : (
                        items.map((c) => (
                          <div
                            key={c.id}
                            className="p-3 rounded-xl bg-white border border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:border-slate-300 transition-all"
                          >
                            {/* Source badge */}
                            <div className="flex items-center gap-1.5 mb-1.5">
                              {c.is_interested && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-pink-50 text-[9px] font-bold text-pink-600 uppercase tracking-wider">
                                  <Heart className="w-2 h-2 fill-pink-500" /> Interested
                                </span>
                              )}
                              {c.source === 'saved' && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-slate-100 text-[9px] font-bold text-slate-600 uppercase tracking-wider">
                                  <Bookmark className="w-2 h-2" /> Saved
                                </span>
                              )}
                              {c.cv_language && (
                                <span className="text-[9px] text-slate-400 font-bold uppercase ml-auto">{c.cv_language}</span>
                              )}
                            </div>

                            {/* Role */}
                            {c.current_role && (
                              <p className="text-[13px] font-semibold text-slate-800 leading-snug truncate">{c.current_role}</p>
                            )}
                            {c.current_level && (
                              <span className="inline-block text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded mt-0.5">{c.current_level}</span>
                            )}

                            {/* Location */}
                            {c.base_locations && (
                              <p className="flex items-center gap-1 text-[11px] text-slate-500 mt-1 truncate">
                                <MapPin className="size-3 shrink-0 text-slate-400" aria-hidden />
                                <span className="truncate">{c.base_locations}</span>
                              </p>
                            )}

                            {/* Job tag */}
                            {c.job_title && c.job_title !== 'General Interest' && c.job_title !== 'Saved' && (
                              <p className="text-[10px] text-slate-400 mt-1 truncate">
                                <Briefcase className="w-2.5 h-2.5 inline mr-0.5 -mt-px" />
                                {c.job_title}
                              </p>
                            )}

                            {/* Seeking */}
                            {c.role_preferences && !c.current_role && (
                              <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{c.role_preferences}</p>
                            )}

                            {/* Actions */}
                            <div className="flex flex-wrap items-center gap-0.5 sm:gap-1 mt-2.5 pt-2 border-t border-slate-100">
                              <button
                                onClick={() => setPassportCandidate(c)}
                                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
                              >
                                <IdCard className="w-3 h-3" /> Passport
                              </button>

                              {c.conversation_id && (
                                <button
                                  onClick={() => navigate(`/recruiter-inbox?conversation=${c.conversation_id}`)}
                                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 transition-all"
                                >
                                  <MessageSquare className="w-3 h-3" /> Chat
                                </button>
                              )}

                              {!c.conversation_id && c.source !== 'inbox' && (
                                <button
                                  onClick={() => navigate(`/candidate-search`)}
                                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-pink-500 hover:text-pink-700 hover:bg-pink-50 transition-all"
                                >
                                  <Send className="w-3 h-3" /> Reach Out
                                </button>
                              )}

                              {companyId && c.explorer_id && (
                                <AtsPushButton
                                  companyId={companyId}
                                  candidateUserId={c.explorer_id}
                                  cvId={c.cv_id}
                                  t={t as any}
                                />
                              )}

                              {/* Status changer for inbox items */}
                              {c.source === 'inbox' && (
                                <div className="ml-auto relative group/status">
                                  <button className="px-1.5 py-1 rounded-lg text-[10px] font-semibold text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all">
                                    {updatingStatus === c.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <ChevronDown className="w-3 h-3" />
                                    )}
                                  </button>
                                  <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border border-slate-200 shadow-xl py-1 z-20 w-44 hidden group-hover/status:block">
                                    {STAGES.filter((s) => s.key !== 'interested' && s.key !== 'saved').map((s) => {
                                      const SIcon = s.icon;
                                      return (
                                        <button
                                          key={s.key}
                                          onClick={() => updateStatus(c, s.key)}
                                          className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium transition-colors ${
                                            c.stage === s.key
                                              ? 'bg-slate-100 text-slate-800'
                                              : 'text-slate-600 hover:bg-slate-50'
                                          }`}
                                        >
                                          <SIcon className="w-3 h-3" /> {s.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      {/* Passport Modal */}
      <AnimatePresence>
        {passportCandidate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/50 p-2 backdrop-blur-sm sm:p-4"
            onClick={() => setPassportCandidate(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="flex h-[90vh] w-full max-w-[600px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <span className="flex items-center gap-1.5 text-sm font-semibold tracking-tight text-slate-900">
                  <IdCard className="size-3.5" /> Passport
                </span>
                <button onClick={() => setPassportCandidate(null)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                  <X className="size-5" />
                </button>
              </div>
              <div className="min-h-0 flex-1">
                <PassportViewer
                  cvId={passportCandidate.cv_id}
                  audience="recruiter"
                  candidateId={passportCandidate.explorer_id}
                  recruiterPrefs={{
                    base_locations: passportCandidate.base_locations,
                    preferred_locations: passportCandidate.preferred_locations,
                    role_preferences: passportCandidate.role_preferences,
                    current_role: passportCandidate.current_role,
                    current_level: passportCandidate.current_level,
                  }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </RecruiterPageShell>
  );
};

export default Pipeline;
